/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { Icon } from '@fluentui/react/lib/Icon';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { ResourceAssignmentForRegionSwagger } from 'src/core/services/resourceAssignmentSwaggers';
import { CustomSkillDeploymentStore } from 'src/core/stores/CustomSkillDeploymentStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { Region } from 'src/core/types/regionTypes';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { getRegionName } from 'src/core/utils/resourceAssignmentUtils';

type DeploymentStore = CustomSkillDeploymentStore | CustomSkillDeploymentStore;

export type DeployedModelsTableProps = TableProps<DeploymentStore> & {
    deletedDeploymentsIds?: string[];
    isMultiRegionDeploymentEnabled?: boolean;
    hiddenColumnsKeys?: ('name' | 'status' | 'date')[];
};

const messages = defineMessages({
    status: { id: 'modelDeploy.deployedModelsTable.status', defaultMessage: 'Status' },
    regions: { id: 'modelDeploy.deployedModelsTable.regions', defaultMessage: 'Regions' },
    deployed: { id: 'modelDeploy.deployedModelsTable.deployed', defaultMessage: 'Deployed' },
    deleting: { id: 'modelDeploy.deployedModelsTable.deleting', defaultMessage: '(deleting)' },
    modelName: { id: 'modelDeploy.deployedModelsTable.modelName', defaultMessage: 'Model name' },
    notDeployed: { id: 'modelDeploy.deployedModelsTable.notDeployed', defaultMessage: 'Not deployed' },
    deploymentFailed: { id: 'modelDeploy.deployedModelsTable.deploymentFailed', defaultMessage: 'Failed' },
    deploymentDate: { id: 'modelDeploy.deployedModelsTable.deploymentDate', defaultMessage: 'Deployed on' },
    expiryDate: { id: 'modelDeploy.deployedModelsTable.expiryDate', defaultMessage: 'Deployment expiry date' },
    modelExpired: { id: 'modelDeploy.deployedModelsTable.modelExpired', defaultMessage: 'Retrain your model' },
    deploymentName: { id: 'modelDeploy.deployedModelsTable.deploymentName', defaultMessage: 'Deployment name' },
    noTrainedModel: { id: 'modelDeploy.deployedModelsTable.noTrainedModel', defaultMessage: 'There are no trained models.' },
    modelAboutToExpire: { id: 'modelDeploy.deployedModelsTable.modelAboutToExpire', defaultMessage: 'Model about to expire' },
    deploymentInProgress: { id: 'modelDeploy.deployedModelsTable.deploymentInProgress', defaultMessage: 'Deployment in progress' }
});

export const DeployedModelsTable = observer((props: DeployedModelsTableProps) => {
    const {
        items,
        sortKey,
        selection,
        hiddenColumnsKeys = [],
        deletedDeploymentsIds = [],
        isMultiRegionDeploymentEnabled = FeatureToggles.MultiRegionDeployments
    } = props;
    const { localize, locale } = useLocalization();
    const theme = getScopedTheme('DeployedModelsTable');

    const sortColumnObject = (field: keyof DeploymentStore) => ({
        isSorted: sortKey?.key === field,
        isSortedDescending: sortKey?.key === field && sortKey?.order === 'desc'
    });

    const getRegionsText = (regions: ResourceAssignmentForRegionSwagger[]) =>
        regions?.length ? regions.map(resource => getRegionName(resource.region as Region)).join(', ') : '--';

    const onRenderDeploymentRegions = (item: DeploymentStore) => (
        <TruncatedText title={getRegionsText(item.assignedResources)}>{getRegionsText(item.assignedResources)}</TruncatedText>
    );

    const onRenderExpiryDate = (item: DeploymentStore) => {
        if (!item.expirationDate) {
            return <LuisText>--</LuisText>;
        }

        const monthBeforeExpiry = item.expirationDate.getTime() - 30 * 24 * 60 * 60 * 1000;

        if (item.expirationDate.getTime() < new Date().getTime()) {
            return (
                <>
                    <LuisText>{item.expirationDate?.toLocaleDateString() ?? '--'}</LuisText>
                    <Icon
                        iconName="ErrorBadge"
                        styles={{
                            root: {
                                color: theme.errorIconColor,
                                marginLeft: theme.expiryIconLeftMargin,
                                marginRight: theme.expiryIconRightMargin
                            }
                        }}
                    />
                    <LuisText styles={{ root: { color: theme.errorIconColor } }}>{localize(messages.modelExpired)}</LuisText>
                </>
            );
        }

        if (monthBeforeExpiry < new Date().getTime()) {
            return (
                <>
                    <LuisText>{item.expirationDate.toLocaleDateString()}</LuisText>
                    <Icon
                        iconName="Warning12"
                        styles={{
                            root: {
                                color: theme.warningIconColor,
                                marginLeft: theme.expiryIconLeftMargin,
                                marginRight: theme.expiryIconRightMargin
                            }
                        }}
                    />
                    <LuisText styles={{ root: { color: theme.warningIconColor } }}>{localize(messages.modelAboutToExpire)}</LuisText>
                </>
            );
        }

        return <LuisText>{item.expirationDate.toLocaleDateString()}</LuisText>;
    };

    const columns: IColumn[] = [
        {
            minWidth: 300,
            maxWidth: 500,
            isResizable: true,
            key: 'deploymentName',
            fieldName: 'deploymentName',
            ...sortColumnObject('deploymentName'),
            name: localize(messages.deploymentName),
            onRender: (item: DeploymentStore) => (
                <LuisText>
                    {item.deploymentName}
                    {deletedDeploymentsIds.includes(item.id) && (
                        <LuisText styles={{ root: { fontStyle: 'italic' } }}>&nbsp;{localize(messages.deleting)}</LuisText>
                    )}
                </LuisText>
            )
        },
        {
            minWidth: 150,
            maxWidth: 200,
            key: 'modelName',
            isResizable: true,
            fieldName: 'modelName',
            ...sortColumnObject('modelName'),
            name: localize(messages.modelName)
        },
        {
            key: 'lastDeployedDateTime',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            name: localize(messages.deploymentDate),
            ...sortColumnObject('lastDeployedDateTime'),
            onRender: (item: DeploymentStore) => (item.lastDeployedDateTime ? item.lastDeployedDateTime.toLocaleDateString(locale) : '--')
        },
        {
            key: 'expiryDate',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            fieldName: 'expiryDate',
            name: localize(messages.expiryDate),
            ...sortColumnObject('expirationDate'),
            onRender: onRenderExpiryDate
        }
    ].concat(
        isMultiRegionDeploymentEnabled
            ? [
                  {
                      key: 'deploymentRegions',
                      minWidth: 250,
                      maxWidth: 300,
                      isResizable: true,
                      fieldName: 'deploymentRegions',
                      name: localize(messages.regions),
                      ...sortColumnObject('assignedResources'),
                      onRender: onRenderDeploymentRegions
                  }
              ]
            : []
    );

    return (
        <Table<DeploymentStore>
            items={items}
            selection={selection}
            selectionMode={SelectionMode.multiple}
            emptyPlaceholder={localize(messages.noTrainedModel)}
            onRenderRow={(p, d) => d({ ...p, disabled: deletedDeploymentsIds.includes((p.item as DeploymentStore).id) })}
            columns={columns.filter(column => hiddenColumnsKeys.every(hiddenColumnKey => hiddenColumnKey !== column.key))}
            {...props}
        />
    );
});
